import {
  type DutyBracket,
  type FirstHomeDutyInput,
  FirstHomeDutyInputSchema,
  type FirstHomeDutyOutput,
  type StampDutyRuleSet,
  type TransferDutyInput,
  TransferDutyInputSchema,
  type TransferDutyOutput,
} from "./types.js";

// ---------------------------------------------------------------------------
// NCCP / financial-product handoff guard (ship-condition, hard-encoded).
//
// Stamp duty is a STATE TAX, not a financial product — but a personalised duty
// figure routed toward a paid broker/lender is credit assistance requiring an
// Australian Credit Licence (NCCP). The panel's binding ship-condition (panels/
// stamp-duty-calculator.md §2, condition 1) is: NO paid handoff to any s764A
// financial product — especially credit / home-loan — may reach ANY surface
// that shows a duty figure. Conveyancing is a legal/settlement service (outside
// AFSL/NCCP) and may sit on a generic off-result footer ONLY.
//
// We encode this as a hard rule, not doc prose: anything attaching a partner/
// handoff to a duty result must route through `assertNoCreditHandoff`, which
// throws for any credit/financial-product/broker/lender identifier. The calc
// functions below carry no partner field by construction — a duty output cannot
// reference a financial product. The guard + its test make the rule executable.
// ---------------------------------------------------------------------------

/** Identifiers that may never ride alongside a personalised duty figure. */
const FORBIDDEN_HANDOFF_PATTERNS = [
  "broker",
  "lender",
  "home loan",
  "home-loan",
  "homeloan",
  "mortgage",
  "loan",
  "credit",
  "refinance",
  "borrowing",
  "pre-approval",
  "preapproval",
  "finance offer",
];

/** Partner verticals that ARE clean (legal/settlement, off-surface only). */
const ALLOWED_PARTNER_VERTICALS = ["conveyancing", "conveyancer", "settlement-agent"];

export class CreditHandoffError extends Error {
  constructor(identifier: string) {
    super(
      `NCCP guard: "${identifier}" is a credit/financial-product handoff and must ` +
        `not reach any surface showing a duty figure (panels/stamp-duty-calculator.md §2).`,
    );
    this.name = "CreditHandoffError";
  }
}

/**
 * Assert that a partner/handoff identifier is permitted on a duty-result
 * surface. Throws `CreditHandoffError` for any credit/financial-product/broker/
 * lender identifier. Returns the (clean) identifier unchanged so call sites can
 * use it inline. This is the executable form of ship-condition 1.
 */
export function assertNoCreditHandoff(identifier: string): string {
  const id = identifier.toLowerCase();
  // Allowed verticals are exempt even if a substring (e.g. nothing matches
  // here, but kept explicit so a future "conveyancing-loan" wouldn't slip).
  const isExplicitlyAllowed = ALLOWED_PARTNER_VERTICALS.some((v) => id === v);
  if (isExplicitlyAllowed) return identifier;
  for (const pattern of FORBIDDEN_HANDOFF_PATTERNS) {
    if (id.includes(pattern)) throw new CreditHandoffError(identifier);
  }
  return identifier;
}

// ---------------------------------------------------------------------------
// Pure duty calculation.
// ---------------------------------------------------------------------------

/** Pick the bracket whose `[from, to)` contains `value`. */
function bracketFor(brackets: readonly DutyBracket[], value: number): DutyBracket {
  for (const b of brackets) {
    if (value >= b.from && (b.to === null || value < b.to)) return b;
  }
  throw new Error(`no stamp-duty bracket matched dutiable value ${value}`);
}

/** Whole-dollar duty for a value within a single bracket. */
function dutyInBracket(b: DutyBracket, value: number): number {
  if (b.rate === 0) return 0;
  if (b.wholeValue) return Math.round(b.rate * value);
  return Math.round(b.base + b.rate * (value - b.over));
}

/** Inclusive bracket label, e.g. "$540,001–$1,000,000" or "$1,000,001 and over". */
function bracketLabel(b: DutyBracket): string {
  const fmt = (n: number) => `$${n.toLocaleString("en-AU")}`;
  if (b.to === null) return `${fmt(b.from)} and over`;
  return `${fmt(b.from)}–${fmt(b.to - 1)}`;
}

/** General transfer duty (no concession, no surcharge), whole dollars. */
export function generalDuty(rules: StampDutyRuleSet, dutiableValue: number): number {
  return dutyInBracket(bracketFor(rules.brackets, dutiableValue), dutiableValue);
}

/**
 * Transfer (stamp) duty payable: general duty + foreign-purchaser surcharge.
 * Pure: same inputs + ruleset always yield the same output. Validates at the
 * boundary.
 */
export function calculateTransferDuty(
  rawInput: TransferDutyInput,
  rules: StampDutyRuleSet,
): TransferDutyOutput {
  const input = TransferDutyInputSchema.parse(rawInput);
  if (input.state !== rules.state) {
    throw new Error(
      `state mismatch: input ${input.state} vs ruleset ${rules.state}`,
    );
  }

  const bracket = bracketFor(rules.brackets, input.dutiableValue);
  const general = dutyInBracket(bracket, input.dutiableValue);
  const foreignSurcharge =
    input.residency === "foreign"
      ? Math.round(rules.foreignSurchargeRate * input.dutiableValue)
      : 0;

  return {
    state: rules.state,
    dutiableValue: input.dutiableValue,
    generalDuty: general,
    foreignSurcharge,
    totalDuty: general + foreignSurcharge,
    bracket: {
      label: bracketLabel(bracket),
      base: bracket.base,
      rate: bracket.rate,
      over: bracket.over,
    },
  };
}

/**
 * First-home duty: the full general duty vs the duty payable WITH the
 * first-home exemption/concession, and the dollar difference between those two
 * CITED figures. Presented as a computed difference between two duty figures —
 * NEVER a "you save $X" claim (the copy layer enforces that framing).
 *
 * QLD new-build branch is checked FIRST: from 1 May 2025 a first-home buyer of a
 * new home pays nil duty with NO value cap; the $700k threshold is for
 * established homes only.
 */
export function calculateFirstHomeDuty(
  rawInput: FirstHomeDutyInput,
  rules: StampDutyRuleSet,
): FirstHomeDutyOutput {
  const input = FirstHomeDutyInputSchema.parse(rawInput);
  if (input.state !== rules.state) {
    throw new Error(
      `state mismatch: input ${input.state} vs ruleset ${rules.state}`,
    );
  }

  const value = input.dutiableValue;
  const full = generalDuty(rules, value);

  // QLD new-build branch — uncapped nil duty from 1 May 2025. Checked before the
  // established-home thresholds so the $700k cap never applies to a new build.
  if (input.buildType === "new" && rules.firstHomeNewBuild) {
    return {
      state: rules.state,
      dutiableValue: value,
      buildType: input.buildType,
      fullDuty: full,
      concessionDuty: 0,
      concessionValue: full,
      concessionApplies: true,
      branch: "qld-new-build",
    };
  }

  const { fullExemptionUpTo, taperEndsAt, taper, maxSaving } = rules.firstHome;

  // Above the taper — full duty, no first-home concession.
  if (value > taperEndsAt) {
    return {
      state: rules.state,
      dutiableValue: value,
      buildType: input.buildType,
      fullDuty: full,
      concessionDuty: full,
      concessionValue: 0,
      concessionApplies: false,
      branch: "none",
    };
  }

  // At/below the full-exemption threshold — nil duty.
  if (value <= fullExemptionUpTo) {
    return {
      state: rules.state,
      dutiableValue: value,
      buildType: input.buildType,
      fullDuty: full,
      concessionDuty: 0,
      concessionValue: full,
      concessionApplies: true,
      branch: "full-exemption",
    };
  }

  // Taper band — reduced duty per the state's taper rule.
  const fraction = (value - fullExemptionUpTo) / (taperEndsAt - fullExemptionUpTo);
  let concessionDuty: number;
  if (taper === "qld-saving") {
    // Saving phases from `maxSaving` at the exemption ceiling to $0 at taperEnd.
    const saving = Math.round((maxSaving ?? 0) * (1 - fraction));
    concessionDuty = Math.max(0, full - saving);
  } else {
    // vic-linear / nsw-fhbas: concessional duty = generalDuty × fraction.
    concessionDuty = Math.round(full * fraction);
  }

  return {
    state: rules.state,
    dutiableValue: value,
    buildType: input.buildType,
    fullDuty: full,
    concessionDuty,
    concessionValue: full - concessionDuty,
    concessionApplies: full - concessionDuty > 0,
    branch: "taper",
  };
}
